import { Grid, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import { type Control, Controller, type FieldErrors } from 'react-hook-form';
import type { CompanyFormData } from '../../schemas/companyInfoSchema';
import { textFieldStyles } from './sharedStyles';

interface Props {
  control: Control<CompanyFormData>;
  errors: FieldErrors<CompanyFormData>;
  isRestricted: boolean;
  formatNumberWithCommas: (value: string | number) => string;
}

export const InsuranceDetailsSection: React.FC<Props> = ({
  control,
  errors,
  isRestricted,
  formatNumberWithCommas,
}) => {
  const theme = useTheme();

  return (
    <Grid>
      <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 'bold' }}>
        Insurance Details
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
        Public Liability Insurance
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="liability_insurer"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Insurer"
                sx={textFieldStyles(theme)}
                error={!!errors.liability_insurer}
                helperText={errors.liability_insurer?.message || `${field.value?.length || 0}/100`}
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="liability_insurance_expiry"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Expiry Date"
                type="date"
                sx={textFieldStyles(theme)}
                error={!!errors.liability_insurance_expiry}
                helperText={errors.liability_insurance_expiry?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="liability_insurance_per_claim"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                onChange={(e) => {
                  const formatted = formatNumberWithCommas(e.target.value);
                  field.onChange(formatted);
                }}
                fullWidth
                label="Limit per Claim"
                placeholder="$0"
                sx={textFieldStyles(theme)}
                error={!!errors.liability_insurance_per_claim}
                helperText={errors.liability_insurance_per_claim?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="liability_insurance_aggregate"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                onChange={(e) => {
                  const formatted = formatNumberWithCommas(e.target.value);
                  field.onChange(formatted);
                }}
                fullWidth
                label="Aggregate Limit"
                placeholder="$0"
                sx={textFieldStyles(theme)}
                error={!!errors.liability_insurance_aggregate}
                helperText={errors.liability_insurance_aggregate?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 'medium' }}>
        Professional Indemnity Insurance
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="indemnity_insurer"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Insurer"
                sx={textFieldStyles(theme)}
                error={!!errors.indemnity_insurer}
                helperText={errors.indemnity_insurer?.message || `${field.value?.length || 0}/100`}
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="indemnity_insurer_expiry"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Expiry Date"
                type="date"
                sx={textFieldStyles(theme)}
                error={!!errors.indemnity_insurer_expiry}
                helperText={errors.indemnity_insurer_expiry?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="indemnity_insurer_per_claim"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                onChange={(e) => {
                  const formatted = formatNumberWithCommas(e.target.value);
                  field.onChange(formatted);
                }}
                fullWidth
                label="Limit per Claim"
                placeholder="$0"
                sx={textFieldStyles(theme)}
                error={!!errors.indemnity_insurer_per_claim}
                helperText={errors.indemnity_insurer_per_claim?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="indemnity_insurer_aggregate"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                onChange={(e) => {
                  const formatted = formatNumberWithCommas(e.target.value);
                  field.onChange(formatted);
                }}
                fullWidth
                label="Aggregate Limit"
                placeholder="$0"
                sx={textFieldStyles(theme)}
                error={!!errors.indemnity_insurer_aggregate}
                helperText={errors.indemnity_insurer_aggregate?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" sx={{ mt: 2, mb: 2, fontWeight: 'medium' }}>
        Workers Compensation
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="workers_compensation_insurer_1"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Insurer 1"
                sx={textFieldStyles(theme)}
                error={!!errors.workers_compensation_insurer_1}
                helperText={
                  errors.workers_compensation_insurer_1?.message ||
                  `${field.value?.length || 0}/100`
                }
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="workers_compensation_expiry_1"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Expiry Date 1"
                type="date"
                sx={textFieldStyles(theme)}
                error={!!errors.workers_compensation_expiry_1}
                helperText={errors.workers_compensation_expiry_1?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="workers_compensation_insurer_2"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Insurer 2"
                sx={textFieldStyles(theme)}
                error={!!errors.workers_compensation_insurer_2}
                helperText={
                  errors.workers_compensation_insurer_2?.message ||
                  `${field.value?.length || 0}/100`
                }
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="workers_compensation_expiry_2"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Expiry Date 2"
                type="date"
                sx={textFieldStyles(theme)}
                error={!!errors.workers_compensation_expiry_2}
                helperText={errors.workers_compensation_expiry_2?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="workers_compensation_insurer_3"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Insurer 3"
                sx={textFieldStyles(theme)}
                error={!!errors.workers_compensation_insurer_3}
                helperText={
                  errors.workers_compensation_insurer_3?.message ||
                  `${field.value?.length || 0}/100`
                }
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="workers_compensation_expiry_3"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Expiry Date 3"
                type="date"
                sx={textFieldStyles(theme)}
                error={!!errors.workers_compensation_expiry_3}
                helperText={errors.workers_compensation_expiry_3?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="workers_compensation_insurer_4"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Insurer 4"
                sx={textFieldStyles(theme)}
                error={!!errors.workers_compensation_insurer_4}
                helperText={
                  errors.workers_compensation_insurer_4?.message ||
                  `${field.value?.length || 0}/100`
                }
                inputProps={{ maxLength: 100 }}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="workers_compensation_expiry_4"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                disabled={isRestricted}
                fullWidth
                label="Expiry Date 4"
                type="date"
                sx={textFieldStyles(theme)}
                error={!!errors.workers_compensation_expiry_4}
                helperText={errors.workers_compensation_expiry_4?.message}
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
